import { Modal } from 'antd-mobile'
import moment from 'moment'
import { call, put, select, takeLatest } from 'redux-saga/effects'
import { setLoading, setLoadingInit } from '../actions/appActions'
import { toggleModal } from '../actions/modalActions'
import {
    actionTypes,
    addToCart,
    checkTableValidSuccess,
    getOrdersQRCodeSuccess,
    requestPaymentSuccess,
    setDeliverPosition,
    setTmpFoods,
    submitOrderSuccess,
    submitOrderTakeAGoSuccess,
    updateOrder,
    updateOrderMethodRedux,
    verifyQRCodeSuccess,
    verifyTableSuccess
} from '../actions/orderActions'
import { ACCESS_SERVICES_MAP, APP_SERVICE, DELIVERY_TIME, FULL_DATE_FORMAT, MODAL_KEYS, ORDER_METHOD, ORDER_METHOD_MAP } from '../constants'
import apiConfig from '../constants/apiConfig'
import i18n from '../locales/i18n'
import { getListFood, getRestaurantId, getRestaurantSettings } from '../selectors'
import { sendRequest } from '../services/apiService'
import { calcMoneyFoodList, getFingerId, getFoodPriceShow, remapFoodToAdd, validFoodDataFromServer } from '../utils'
import {
    ensureArray,
    errorHandle,
    showMessageError,
    showMessageWarn
} from '../utils/helper'
import { getCurrentDistanceData } from '../utils/order'

const alert = Modal.alert

// check qrcode table valid when reload
function* _checkTableValid({ payload: { onDone } }) {
    try {
        yield put(setLoadingInit(true))

        const { tableID, hash } = yield select(state => state.OrderReducer)
        if (!tableID && !hash) {
            return onDone && onDone(true)
        }
        const finger = yield getFingerId();
        const response = yield call(sendRequest, apiConfig.order.verifyTable, {
            hash,
            id: tableID,
            finger
        })

        const { success, responseData } = response
        if (!success) {
            throw 'invalid table'
        }

        yield put(checkTableValidSuccess(responseData?.data))

        onDone && onDone(response?.responseData?.result)
    } catch (error) {
        alert('', i18n.t('message.invalidTable'), [])
        onDone && onDone(false)
    } finally {
        yield put(setLoadingInit(false))
    }
}

// verify qrcode table when scan
function* _verifyTable({ payload: { params, onDone } }) {
    try {
        yield put(setLoadingInit(true))

        const response = yield call(
            sendRequest,
            apiConfig.order.verifyTable,
            params
        )

        const { responseData, success } = response

        if (!success) {
            throw responseData?.code === 'ERROR-ORDER-0013' ? 'message.alreadyScan' : 'message.invalidTable';
        }

        yield put(verifyTableSuccess({ ...params, ...responseData.data }));
        if (responseData?.data?.customerPosition === undefined) {
            yield put(toggleModal({
                modal: MODAL_KEYS.tableQrcodeQuestion,
                isOpen: true
            }));
        }

        onDone && onDone(response)
    } catch (error) {
        console.log(error);
        alert('', i18n.t(error), [])
    } finally {
        yield put(setLoadingInit(false))
    }
}

// verify qrcode at restaurant
function* _verifyTabletQRCode({ payload: { params, onDone } }) {
    try {
        yield put(setLoadingInit(true))

        const response = yield call(
            sendRequest,
            {
                ...apiConfig.order.verifyQRCode,
                path:
                    apiConfig.order.verifyQRCode.path +
                    '?hash=' +
                    (params?.hash || ''),
            },
            {}
        )
        console.log({ response })

        const { responseData, success } = response

        if (
            !responseData?.result &&
            responseData?.code === 'ERROR-ORDER-0004'
        ) {
            return alert('', i18n.t('message.tabletQRCodeScannedBySomeone'), [])
        }

        if (!success) {
            throw 'invalid table'
        }

        yield put(
            verifyQRCodeSuccess({
                ...params,
                ...responseData.data,
                firebaseUrl: responseData.firebaseUrl,
            })
        )

        onDone && onDone(response)
    } catch (error) {
        alert('', i18n.t('message.invalidQRCode'), [])
    } finally {
        yield put(setLoadingInit(false))
    }
}

// qrcode at table submit
function* _submitOrder({ payload: { params, onDone } }) {
    const { isTakeAGo } = params || {}
    try {
        yield put(setLoading(true))
        const {
            orderMethod,
            tokenOrderForWeb,
            customerPosition,
            ordering,
            tabletForm,
        } = yield select(state => state.OrderReducer)

        const orderingFiltered = ordering.filter(el => !el.submitted)

        const { currentcy, allow_view_order } = yield select(
            getRestaurantSettings
        )
        const foodRemaped = remapFoodToAdd(
            orderingFiltered,
            currentcy,
            customerPosition,
        )
        const { total: totalMoney } = calcMoneyFoodList(
            orderingFiltered.filter(f => !f.submitted),
            orderMethod
        )
        const finger = yield getFingerId();
        const payload = {
            totalMoney: Math.ceil(totalMoney * 100),
            foodData: foodRemaped,
            paymentMethod: tabletForm?.paymentMethod,
            typeOrder: tabletForm?.orderMethod,
            finger
        }

        customerPosition && (payload.customerPosition = customerPosition)

        const options = isTakeAGo
            ? { ...apiConfig.order.addToCartTakeAGo }
            : { ...apiConfig.order.addToCart }
        options.headers.Authorization = `Bearer ${tokenOrderForWeb}`
        const response = yield call(sendRequest, options, payload)

        const { responseData, success } = response

        if (!success) {
            throw responseData
        }

        const nextAction = isTakeAGo
            ? submitOrderTakeAGoSuccess
            : submitOrderSuccess

        // add to redux
        yield put(
            nextAction({
                customerPosition: responseData.data.customerPosition,
                allowViewOrder: allow_view_order,
                foodData: foodRemaped,
            })
        )

        onDone && onDone(response)
    } catch (error) {
        console.log({ error })
        errorHandle({ message: 'apiMessage.saveOrderFailed' })
    } finally {
        yield put(setLoading(false))
    }
}

function* _saveOrder({ payload: { params, onDone } }) {
    try {
        yield put(setLoading(true))

        const { ordering, orderMethod } = yield select(state => state.OrderReducer);
        const restaurantId = yield select(getRestaurantId);
        const { total: totalMoney } = calcMoneyFoodList(ordering)
        const { currentcy, allow_view_order, timezone } = yield select(
            getRestaurantSettings
        )
        const { deliverPosition } = yield select(state => state.OrderReducer);
        const {
            customer: { paymentMethod, userAddressNote, deliveryTime, ...rest },
            voucherId,
        } = params

        const distanceRequireData = getCurrentDistanceData((deliverPosition?.distance || 0) / 1000);

        const { latitude, longitude } = deliverPosition || {};

        const foodRemaped = remapFoodToAdd(ordering, currentcy)
        let timeDeliverFull;
        if (!!deliveryTime && deliveryTime !== DELIVERY_TIME) {
            const [ hour, minutes ] = deliveryTime.split(':')
            timeDeliverFull = moment(window.appTime).hours(hour).minutes(minutes).format(FULL_DATE_FORMAT)
        }
        const payload = {
            orderNote: userAddressNote,
            kind: ORDER_METHOD_MAP[orderMethod],
            paymentMethod,
            restaurantId,
            foodData: foodRemaped,
            ...(voucherId && { voucherId }),
            customer: {
                ...rest,
                timeDeliver:
                    deliveryTime === DELIVERY_TIME ? undefined : deliveryTime,
                timeDeliverFull,
                ...(latitude !== undefined && { latitude }),
                ...(longitude !== undefined && { longitude }),
            },
            totalMoney: Math.round(totalMoney * 100),
            shipMoney: orderMethod === ORDER_METHOD.DELIVERY ? distanceRequireData?.ship_money || 0 : undefined
        }
        const { success, responseData } = yield call(
            sendRequest,
            apiConfig.order.saveOrder,
            payload
        )

        if (!success) {
            throw 'invalid table'
        }

        // add to redux
        yield put(
            submitOrderSuccess({
                customerPosition: responseData.data?.customerPosition,
                allowViewOrder: allow_view_order,
            })
        )

        onDone && onDone({ success, responseData })
    } catch (error) {
        // alert('', i18n.t('message.invalidTable'), [])
        errorHandle({ message: 'apiMessage.saveOrderFailed' })
    } finally {
        yield put(setLoading(false))
    }
}

function* _addFoodGroup({ payload: { params, onDone } }) {
    const { arrID, vat, ...formData } = params

    const foods = yield select(getListFood)
    const foodSelected = foods.filter(f => arrID.includes(String(f.id)))
    let countFoodRequireBeilage = 0

    const foodMaped = foodSelected.map((f, index) => {
        const { currentPrice, saleOfPercent, isHappyHour } = getFoodPriceShow(f)
        const { beilageItems } = f

        const foodData = {
            ...formData,
            vat,
            beilageItems: ensureArray(beilageItems).some(bei => !!bei.option)
                ? undefined
                : [],
            foodDetails: {
                ...f,
                currentPrice: f.currentPrice ?? currentPrice,
                saleOfPercent,
            },
            isHappyHour,
            tmpIndex: index,
        }
        !foodData.beilageItems && countFoodRequireBeilage++

        return foodData
    })

    const canAddImmediately = !countFoodRequireBeilage
    if (canAddImmediately) {
        yield put(addToCart(foodMaped))
    } else {
        yield put(setTmpFoods(foodMaped))
    }

    onDone && onDone(canAddImmediately)
}

function* _requestPayment({ payload: { params, onDone, backgroundMode } }) {
    let isSuccess = false
    try {
        if (!backgroundMode) {
            yield put(setLoading(true));
        }

        const { tokenOrderForWeb } = yield select(state => state.OrderReducer)

        const options = { ...apiConfig.order.requestPayment }
        options.headers.Authorization = `Bearer ${tokenOrderForWeb}`
        options.path = options.path + '?kind=' + params.kind;
        const { success, responseData } = yield call(sendRequest, options)

        if (success) {
            isSuccess = true
            yield put(requestPaymentSuccess({
                kind: params.kind
            }))
        } else {
            throw 'error'
        }
    } catch (error) {
        showMessageError(i18n.t('apiMessage.customerRequestPaymentFailed'))
    } finally {
        if (!backgroundMode) {
            yield put(setLoading(false));
        }
    }

    onDone && onDone(isSuccess)
}

function* _searchLocation({ payload: { params, onDone } }) {
    try {
        const { map_key_direction, region_google } = yield select(
            state => state.RestaurantReducer.settings.deliver_setting
        )
        const { address } = params

        const response = yield call(
            sendRequest,
            apiConfig.order.searchLocation,
            {
                key: map_key_direction || ' ',
                sensor: false,
                language: 'en',
                address: `${address}+${region_google}`.replace(/ /g, '+'),
            }
        )

        onDone && onDone(response)
    } catch (error) {}
}

function* _geocodeAddress(address, map_key_direction) {
    try {
        const { success, responseData } = yield call(
            sendRequest,
            apiConfig.order.searchLocation,
            {
                key: map_key_direction || ' ',
                sensor: false,
                language: 'en',
                address,
            }
        )
        if (success && responseData?.results?.length) {
            const { lat, lng } = responseData.results[0].geometry.location
            return { latitude: lat, longitude: lng }
        }
    } catch (error) {}
    return {}
}

function* _calcDistance({ payload: { params, onDone, onError } }) {
    try {
        const { map_key_direction } = yield select(
            state => state.RestaurantReducer.settings.deliver_setting
        )
        yield put(setLoading(true))

        const { latitude, longitude } = yield select(
            state => state.RestaurantReducer
        )
        const { destinations, skipSetDeliverPosition, ...rest } = params

        const { success, responseData } = yield call(
            sendRequest,
            apiConfig.order.calcDistance,
            {
                origins: `${latitude},${longitude}`,
                destinations,
                language: 'en',
                key: map_key_direction || ' ',
                mode: 'driving',
                sensor: true,
            }
        )
        console.log({ success, responseData })

        if (success) {
            const { rows, destination_addresses, error_message } = responseData
            const name = destination_addresses[0]

            if (error_message) {
                showMessageWarn(error_message);
            } else if (!name || rows?.[0]?.elements?.[0]?.status !== 'OK') {
                showMessageWarn(i18n.t('message.invalidAddress'));
                yield put({
                    type: actionTypes.CLEAR_DISTANCE
                });
                return onError && onError();
            }

            const geocodeResult = (rest.latitude === undefined) ? yield _geocodeAddress(destinations, map_key_direction || ' ') : {};

            const payload = {
                name,
                distance: rows[0].elements[0].distance.value,
                ...rest,
                ...geocodeResult,
            }

            !skipSetDeliverPosition && (yield put(setDeliverPosition(payload)))

            onDone && onDone(payload)
        }
    } catch (error) {
    } finally {
        yield put(setLoading(false))
    }
}

function* _getOrderDetails({ payload: { params, onDone } }) {
    try {
        yield put(setLoading(true))

        const response = yield call(
            sendRequest,
            {
                ...apiConfig.order.orderDetails,
                path: `${apiConfig.order.orderDetails.path}/${params.orderID}`,
            },
            {}
        )

        onDone && onDone(response)
    } catch (error) {
    } finally {
        yield put(setLoading(false))
    }
}

function* _getOrdersQRCode({ payload: { params, onError } }) {
    try {
        const { tableID, tokenOrderForWeb, customerPosition } =
            yield select(state => state.OrderReducer)
        if (!customerPosition) {
            return
        }

        yield put(setLoading(true))

        const finger = yield getFingerId();

        const options = {
            ...apiConfig.order.ordersQRCode,
            path: `${apiConfig.order.ordersQRCode.path}/${tableID}/${finger}`,
        }
        options.headers.Authorization = `Bearer ${tokenOrderForWeb}`

        const { success, responseData } = yield call(sendRequest, options, {})
        if (success && responseData.data) {
            const { foodData } = responseData.data

            yield put(
                getOrdersQRCodeSuccess(
                    ensureArray(foodData)
                        .filter(f => f.customerNo === customerPosition)
                        .map(el => {
                            el.submitted = true
                            el.groupOrder = +el.groupOrder
                            return el
                        })
                )
            )
            yield put(getOrdersQRCodeSuccess(validFoodDataFromServer(foodData, customerPosition)))
        } else {
            onError && onError()
        }
    } catch (error) {
        console.log(error)
    } finally {
        yield put(setLoading(false))
    }
}

function* _closeNotiDoneOrder({ payload: { params, onDone } }) {
    try {
        const { id } = yield select(state => state.RestaurantReducer)

        const response = yield call(
            sendRequest,
            {
                ...apiConfig.order.closeNotiDoneOrder,
                path: `${apiConfig.order.closeNotiDoneOrder.path}/${params.orderID}`,
            },
            {}
        )

        onDone && onDone(response)
    } catch (error) {}
}

function* _getOrderInfo({ payload: { params, onDone } }) {
    try {
        yield put(setLoading(true));

        const response = yield call(
            sendRequest,
            {
                ...apiConfig.order.orderInfo,
                path: `${apiConfig.order.orderInfo.path}/${params.orderID}`,
            },
            {}
        )

        onDone && onDone(response)
    } catch (error) {
        console.log(error)
    } finally {
        yield put(setLoading(false))
    }
}

function* _payOther({ payload: { params, onDone } }) {
    try {
        yield put(setLoading(true))

        const { orderID, payMethod } = params

        const response = yield call(
            sendRequest,
            {
                ...apiConfig.order.payOther,
                path: `${apiConfig.order.payOther.path}/${orderID}/${payMethod}`,
            },
            {}
        )

        onDone && onDone(response)
    } catch (error) {
        console.log(error)
    } finally {
        yield put(setLoading(false))
    }
}

function* _setOrderMethod({ payload }) {
    try {
        yield put(updateOrderMethodRedux({
            method: payload.method
        }));

        let orderingUpdate = null;
        const ordering = yield select(state => state.OrderReducer.ordering);
        if (APP_SERVICE === ACCESS_SERVICES_MAP.WEB && ordering?.length) {

            orderingUpdate = ordering.map(el => {
                const newFood = JSON.parse(JSON.stringify(el));
                newFood.foodDetails.currentPrice = null;
                newFood.foodDetails.saleOfPercent = null;

                const { currentPrice, saleOfPercent } = getFoodPriceShow(newFood.foodDetails);
                newFood.foodDetails.currentPrice = currentPrice;
                newFood.foodDetails.saleOfPercent = saleOfPercent;

                return newFood
            });

            yield put(updateOrder({
                orderingUpdate
            }));
        }
    } catch (error) {
        console.log(error)
    }
}

const sagas = [
    takeLatest(actionTypes.CHECK_TABLE_VALID, _checkTableValid),
    takeLatest(actionTypes.VERIFY_TABLE, _verifyTable),
    takeLatest(actionTypes.VERIFY_QRCODE, _verifyTabletQRCode),
    takeLatest(actionTypes.ADD_FOOD_GROUP, _addFoodGroup),
    takeLatest(actionTypes.SUBMIT_ORDER, _submitOrder),
    takeLatest(actionTypes.REQUEST_PAYMENT, _requestPayment),
    takeLatest(actionTypes.SEARCH_LOCATION, _searchLocation),
    takeLatest(actionTypes.CALC_DISTANCE, _calcDistance),
    takeLatest(actionTypes.SAVE_ORDER, _saveOrder),
    takeLatest(actionTypes.GET_ORDER_DETAILS, _getOrderDetails),
    takeLatest(actionTypes.GET_ORDERS_QRCODE, _getOrdersQRCode),
    takeLatest(actionTypes.CLOSE_NOTI_DONE_ORDER, _closeNotiDoneOrder),
    takeLatest(actionTypes.GET_ORDER_INFO, _getOrderInfo),
    takeLatest(actionTypes.PAY_OTHER, _payOther),
    takeLatest(actionTypes.SET_ORDER_METHOD, _setOrderMethod),
]

export default sagas
